/**
 * 会员卡业务
 */

import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import intl from 'react-intl-universal';

import { getCdnHost } from '@/config/apiHosts';
import { Calculator, parseFen2Yuan } from '@/utils/number';
import { DrawerContext, createMapRender, PaymentChannelInfo } from '@/pages/handover/helper';
import { fetchHandoverMemberCardReport } from '@/api/handover';
import { getMemberCardListWithCache } from '@/api/cacheRequest';

import IndicatorList, { IndicatorItem } from '../IndicatorList';
import BillChannelTable from '../BillChannelTable';

interface CardInfo {
  cardCode: string;
  buyNum: number; // 售卖
  refundNum: number; // 退卡
  totalNum?: number; // 净售卖
}

interface ReportData {
  memberBuyDTOS: CardInfo[];
  baseBusPaymentTypeMsgDTOS: PaymentChannelInfo[];
  orderNum: number;
  indicatorList: IndicatorItem[];
}

const Index = () => {
  const { setLoadingStatus, handoverId } = useContext(DrawerContext);
  const [reportData, setReportData] = useState<ReportData>({} as ReportData);
  const [cardNameMap, setCardNameMap] = useState({});

  const columns: ColumnsType<CardInfo> = [
    {
      title: intl.get('handover_card_sale_column_code'),
      dataIndex: 'cardCode',
      render: createMapRender(cardNameMap),
    },
    { title: intl.get('handover_card_sale_column_buy'), dataIndex: 'buyNum', align: 'right' },
    { title: intl.get('handover_card_sale_column_refund'), dataIndex: 'refundNum', align: 'right' },
    { title: intl.get('handover_card_sale_column_total'), dataIndex: 'totalNum', align: 'right' },
  ];

  const getMemberCardConfig = async () => {
    try {
      const res = await getMemberCardListWithCache();
      const nameMap = {};
      res.data.forEach((item) => {
        nameMap[item.cardCode] = item.name;
      });
      setCardNameMap(nameMap);
    } catch (e) {
      //
    }
  };

  const getData = async () => {
    setLoadingStatus(true);
    try {
      getMemberCardConfig();
      const res = await fetchHandoverMemberCardReport({ id: handoverId });
      const { memberBuyDTOS, baseBusPaymentTypeMsgDTOS } = res.data as ReportData;
      const cardSaleCount = new Calculator(0);
      const cardSaleIncome = new Calculator(0);
      const updateCardList = (memberBuyDTOS || []).map((item) => {
        const itemVal = new Calculator(0);
        itemVal.plus(item.buyNum || 0).minus(item.refundNum || 0);
        cardSaleCount.plus(itemVal.getValue());
        return {
          ...item,
          totalNum: itemVal.getValue(),
        };
      });
      (baseBusPaymentTypeMsgDTOS || []).forEach((item) => {
        cardSaleIncome.plus(item.income || 0).minus(item.refund || 0);
      });
      setReportData({
        ...res.data,
        memberBuyDTOS: updateCardList,
        indicatorList: [
          // 会员卡售卖数量: 所有卡净售卖数量合计
          {
            name: intl.get('handover_card_indicator_sale_count'),
            label: intl.get('handover_card_indicator_sale_count'),
            icon: `${getCdnHost()}/image/common/20230403/165810098-handover_card_count.png`,
            iconHeight: 34,
            content: cardSaleCount.getValue(),
          },
          // 会员卡售卖金额: 所有卡收退款合计
          {
            name: intl.get('handover_card_indicator_sale_income'),
            label: intl.get('handover_card_indicator_sale_income'),
            icon: `${getCdnHost()}/image/common/20230403/165810092-handover_card_money.png`,
            content: parseFen2Yuan(cardSaleIncome.getValue()),
          },
        ],
      });
    } catch (e) {
      //
    }
    setLoadingStatus(false);
  };

  const renderSummary = () => {
    const buyCountVal = new Calculator(0);
    const refundCountVal = new Calculator(0);

    (reportData.memberBuyDTOS || []).forEach((item) => {
      buyCountVal.plus(item.buyNum || 0);
      refundCountVal.plus(item.refundNum || 0);
    });

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={1}>
          <div className="handover-table-summary-title">
            {intl.get('handover_table_summary_total')}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          {buyCountVal.getValue()}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          {refundCountVal.getValue()}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          {buyCountVal.minus(refundCountVal.getValue()).getValue()}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <div>
      <IndicatorList items={reportData.indicatorList} />
      <div>
        <div className="groupLabel">{intl.get('handover_card_sale_block_title')}</div>
        <Table
          dataSource={reportData.memberBuyDTOS}
          columns={columns}
          summary={renderSummary}
          pagination={false}
        />
      </div>
      <BillChannelTable dataSource={reportData.baseBusPaymentTypeMsgDTOS} />
    </div>
  );
};

export default Index;
